import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FormGenerator from '@sharedCR/common/formGenerator';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import FeeProviderForm from 'app/shared/modules/FeeProviderForm';
import FeeProviderLocationTable from 'app/shared/modules/FeeProviderLocationTable';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { AttendantCompanyTypeEnum } from 'constants/loanApplication';
import useForm from 'hooks/useForm';
import { getCleanPhoneNumber, getConditionalObject } from 'utils/data';
import { getChangedFieldsByConfig, getConfigInfo } from 'utils/loanManagement';

import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  editClosingCompanyDataAction,
  getAttendantCompaniesAction,
} from 'store/loanApplications/actions';
import {
  selectAttendantCompaniesFeeProviderCategoryIdMap,
  selectAttendantCompaniesOptions,
} from 'store/loanApplications/selectors';

import {
  ADD_NEW_FEE_PROVIDER,
  ATTORNEY_COMPANY_TYPE,
} from '../ClosingCompany/ClosingCompany.constants';
import {
  checkIsFeeProviderMissedFields,
  checkIsLocationEmpty,
  getAttorneysValuesToSend,
  getClosingCompanyValuesToSend,
  getInitialApplicationData,
} from '../ClosingCompany/ClosingCompany.utils';
import { LoanClosingTestIds } from '../LoanClosing.testIds';

import { getClosingCompanyEditorConfig } from './ClosingCompanyForm.config';

const ClosingCompanyForm = ({
  isLoading,
  closingCompany,
  applicationId,
  pendingChangesData,
  setIsLoading,
  onExitEditMode,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const sharedClasses = useMemo(() => getSharedFormStyles(theme), [theme]);
  const rootContainerSx = useMemo(
    () => ({ ...sharedClasses.root, ...sharedClasses.container }),
    [sharedClasses],
  );

  const companyOptions = useSelector(selectAttendantCompaniesOptions);
  const categoriesIds = useSelector(selectAttendantCompaniesFeeProviderCategoryIdMap);

  const { closingCompanyType } = closingCompany;
  const isAddAttorneys = closingCompanyType === ATTORNEY_COMPANY_TYPE;
  const isSellersAttorney = closingCompanyType === AttendantCompanyTypeEnum.SELLER_ATTORNEY;
  const isBuyersAttorney = closingCompanyType === AttendantCompanyTypeEnum.BUYER_ATTORNEY;
  const categoryId = isAddAttorneys
    ? categoriesIds[AttendantCompanyTypeEnum.BUYER_ATTORNEY]
    : categoriesIds[closingCompanyType];

  const { fieldsConfig, initialFormValues } = useMemo(() => {
    const applicationData = getInitialApplicationData({
      isAddAttorneys,
      closingCompany,
    });

    return getConfigInfo({
      applicationData,
      config: getClosingCompanyEditorConfig({
        categoryId,
        companyOptions,
        closingCompanyType:
          isAddAttorneys || isSellersAttorney || isBuyersAttorney
            ? ATTORNEY_COMPANY_TYPE
            : applicationData.closingCompanyType,
        isAddAttorneys,
        isSellersAttorney,
        isBuyersAttorney,
      }),
      isNestedForm: isAddAttorneys,
    });
  }, [
    isAddAttorneys,
    closingCompany,
    companyOptions,
    isSellersAttorney,
    isBuyersAttorney,
    categoryId,
  ]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setForm,
    setInForm,
    validateField,
    isFormChanged,
    validateForm,
    setIsTouched,
  } = useForm(
    isAddAttorneys
      ? initialFormValues
      : {
          ...initialFormValues,
          attendantCompanyLocationId: closingCompany.attendantCompanyLocationId,
        },
    fieldsConfig,
  );

  const onChooseLocation = useCallback(
    (__, { location }, sectionPath) => {
      dispatch(closeDialogAction());

      setIsTouched(true);
      setForm(prev =>
        isAddAttorneys
          ? {
              ...prev,
              [sectionPath]: {
                ...prev[sectionPath],
                ...location,
                attendantCompanyLocationId: location.feeProviderLocationId,
                phone: getCleanPhoneNumber(location.phone),
                contactPhone: getCleanPhoneNumber(location.contactPhone),
              },
            }
          : {
              ...prev,
              ...location,
              attendantCompanyLocationId: location.feeProviderLocationId,
              ...getConditionalObject(isSellersAttorney || isBuyersAttorney, () => ({
                phone: getCleanPhoneNumber(location.phone),
                contactPhone: getCleanPhoneNumber(location.contactPhone),
              })),
            },
      );
    },
    [dispatch, setIsTouched, setForm, isAddAttorneys, isSellersAttorney, isBuyersAttorney],
  );

  const onOpenFeeProviderLocationTable = useCallback(
    (option, sectionPath) => {
      const onChangeLocationCallback = (...props) =>
        onOpenFeeProviderLocationTable(...props, sectionPath);
      const onLocationRowCustomClick = (...props) => onChooseLocation(...props, sectionPath);

      dispatch(
        openDialogAction({
          header: 'Choose location',
          size: ModalSize.FULL,
          body: dialogProps => (
            <div className="p-32">
              <FeeProviderLocationTable
                {...dialogProps}
                feeProviderId={option?.id || form.attendantCompanyId?.value}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                onChangeLocationCallback={onChangeLocationCallback}
                onLocationRowCustomClick={onLocationRowCustomClick}
              />
            </div>
          ),
        }),
      );
    },
    [dispatch, isLoading, setIsLoading, onChooseLocation, form.attendantCompanyId?.value],
  );

  const onAttendantCompanyChange = useCallback(
    (e, option, sectionPath) => {
      const onUpdateForm = option => {
        setIsTouched(true);

        const valueToSet = {
          attendantCompanyId: option,
          name: option.name,
        };

        setForm(prev =>
          isAddAttorneys
            ? {
                ...prev,
                [sectionPath]: valueToSet,
              }
            : valueToSet,
        );

        if (option.locations.length === 1) {
          return onChooseLocation(null, { location: option.locations[0] }, sectionPath);
        }

        onOpenFeeProviderLocationTable(option, sectionPath);
      };

      if (option.value === ADD_NEW_FEE_PROVIDER) {
        const onSuccessCreate = payload =>
          dispatch(getAttendantCompaniesAction()).then(() => {
            dispatch(closeDialogAction());
            onUpdateForm({ ...payload, value: payload.feeProviderId });
          });

        return dispatch(
          openDialogAction({
            header: 'Add Fee Provider',
            isRestyled: true,
            size: ModalSize.LARGE,
            withDivider: true,
            hasScrollY: false,
            body: dialogProps => (
              <FeeProviderForm
                isDialog
                feeProviderCategoryId={option.feeProviderCategoryId}
                isLocationsIncluded={false}
                onSuccessCreate={onSuccessCreate}
                {...dialogProps}
              />
            ),
          }),
        );
      }

      onUpdateForm(option);
    },
    [
      setIsTouched,
      isAddAttorneys,
      setForm,
      onOpenFeeProviderLocationTable,
      onChooseLocation,
      dispatch,
    ],
  );

  const { schema, initialConfig } = useMemo(() => {
    const applicationData = getInitialApplicationData({
      isAddAttorneys,
      closingCompany,
    });

    return getConfigInfo({
      applicationData,
      config: getClosingCompanyEditorConfig({
        companyOptions,
        closingCompanyType:
          isAddAttorneys || isSellersAttorney || isBuyersAttorney
            ? ATTORNEY_COMPANY_TYPE
            : applicationData.closingCompanyType,
        onAttendantCompanyChange,
        onOpenFeeProviderLocationTable,
        isChangeLocationButtonDisabled:
          isAddAttorneys || isSellersAttorney || isBuyersAttorney
            ? undefined
            : isLoading || !form.attendantCompanyId?.value,
        isAddAttorneys,
        isSellersAttorney,
        isBuyersAttorney,
        categoryId,
      }),
      isNestedForm: isAddAttorneys,
    });
  }, [
    isAddAttorneys,
    closingCompany,
    companyOptions,
    categoryId,
    isSellersAttorney,
    isBuyersAttorney,
    onAttendantCompanyChange,
    onOpenFeeProviderLocationTable,
    isLoading,
    form.attendantCompanyId?.value,
  ]);

  const { changedFields } = useMemo(
    () => getChangedFieldsByConfig({ pendingChangesData, initialConfig }),
    [initialConfig, pendingChangesData],
  );

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    if (!companyOptions.length) {
      dispatch(showMessageAction({ message: 'No companies found', variant: 'error' }));
    }
  }, [dispatch, companyOptions]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      const isLocationEmpty = checkIsLocationEmpty({ form, isAddAttorneys });
      const isFeeProviderMissedFields = checkIsFeeProviderMissedFields({ form, isAddAttorneys });

      if (isLocationEmpty) {
        return dispatch(showMessageAction({ message: 'Choose location first', variant: 'error' }));
      }

      if (isFeeProviderMissedFields) {
        return dispatch(
          showMessageAction({
            message:
              'Fee Provider selected as Company is missing required information and cannot be used',
            variant: 'error',
          }),
        );
      }

      const closingAttendantCompanies =
        isAddAttorneys || isSellersAttorney || isBuyersAttorney
          ? getAttorneysValuesToSend({ form, isSellersAttorney, isBuyersAttorney })
          : getClosingCompanyValuesToSend({ form, closingCompany });

      setIsLoading(true);

      dispatch(
        editClosingCompanyDataAction({
          applicationId,
          closingAttendantCompanies,
          requestMetadata: { tabId: LoanApplicationTabId.CLOSING },
        }),
      )
        .then(() => onExitEditMode())
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [
      applicationId,
      closingCompany,
      dispatch,
      form,
      isAddAttorneys,
      isBuyersAttorney,
      isSellersAttorney,
      onExitEditMode,
      setIsLoading,
      validateForm,
    ],
  );

  return (
    <Box sx={rootContainerSx}>
      <form noValidate onSubmit={onSubmit}>
        <Box sx={sharedClasses.buttonsWrapper}>
          <Button
            color="primary"
            data-testid={LoanClosingTestIds.CLOSING_COMPANY_FORM_SUBMIT_BUTTON}
            disabled={!isFormChanged || isLoading}
            sx={sharedClasses.submitButton}
            type="submit"
            variant="contained"
          >
            Done
          </Button>
          <Button
            data-testid={LoanClosingTestIds.CLOSING_COMPANY_FORM_CANCEL_BUTTON}
            sx={sharedClasses.cancelButton}
            type="button"
            variant="contained"
            onClick={onExitEditMode}
          >
            Cancel
          </Button>
        </Box>

        <FormGenerator
          changedFields={changedFields}
          config={initialConfig}
          form={form}
          formName={FormNames.CLOSING_COMPANY_INFORMATION}
          handleChange={handleChange}
          isLoading={isLoading}
          isNestedForm={isAddAttorneys}
          numberOfColumns={3}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </form>
    </Box>
  );
};

ClosingCompanyForm.propTypes = {
  ...WithLoadingPropTypes,
  closingCompany: PropTypes.object.isRequired,
  applicationId: PropTypes.string.isRequired,
  pendingChangesData: PropTypes.object.isRequired,
  onExitEditMode: PropTypes.func.isRequired,
};

export default memo(ClosingCompanyForm);
